import { useROIRoleBasedRoutes } from '../../../hooks/use-roi-role-based-routes';

/**
 * Interface defining granular permissions for HingeConnect Rules functionality.
 * Provides fine-grained control over what actions a user can perform on rules.
 */
interface RulesPermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCopy: boolean;
  canCreate: boolean;
  canRunAssessment: boolean;
}

/**
 * Custom hook for rules-specific permissions.
 *
 * This hook provides granular permission checks for rules functionality,
 * separating view access from edit access. It's the single source of truth
 * for rules permissions throughout the application.
 *
 * @returns RulesPermissions object with boolean flags for each rule action
 *
 * @example
 * ```typescript
 * const permissions = useRulesPermissions();
 *
 * // Check if user can view rules
 * if (permissions.canView) {
 *   // Render rules list
 * }
 *
 * // Check if user can edit rules
 * if (permissions.canEdit) {
 *   // Show edit button
 * }
 * ```
 */
export const useRulesPermissions = (): RulesPermissions => {
  const routeAccess = useROIRoleBasedRoutes();

  return {
    canView: routeAccess.hingeConnectRules,
    canEdit: routeAccess.hingeConnectRulesEdit,
    canDelete: routeAccess.hingeConnectRulesDelete,
    canCopy: routeAccess.hingeConnectRulesCreate, // Copy creates a new rule
    canCreate: routeAccess.hingeConnectRulesCreate,
    canRunAssessment: routeAccess.hingeConnectRuleAssessmentsCreate, // Running a rule creates an assessment
  };
};
